import type { QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { z } from "zod";
import { RouteError, RoutePending } from "./components/RouteError";
import { ApiError, fetchMe } from "./api/client";
import { AppShell } from "./components/AppShell";
import { ADMIN_TABS, AdminPage } from "./features/admin/AdminPage";
import { LoginPage } from "./features/auth/LoginPage";
import { CandidatesPage } from "./features/candidates/CandidatesPage";
import { CustomersPage } from "./features/customers/CustomersPage";
import { HomePage } from "./features/home/HomePage";
import { ProjectPage } from "./features/projects/ProjectPage";
import { ProjectsPage } from "./features/projects/ProjectsPage";

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: z.object({ error: z.string().optional(), state: z.string().optional() }),
  component: LoginPage,
});

/** ログインが必要な画面の共通親。未ログインならログイン画面へ。 */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData({ queryKey: ["me"], queryFn: fetchMe });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) throw redirect({ to: "/login" });
      throw e;
    }
  },
  component: AppShell,
});

export const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: HomePage,
});
export const projectsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projects",
  validateSearch: z.object({ status: z.enum(["ACTIVE", "ENDED", "ARCHIVED"]).optional() }),
  component: ProjectsPage,
});
export const projectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projects/$projectId",
  component: ProjectPage,
});
const candidatesSearch = z.object({
  status: z.enum(["PENDING", "APPROVED", "REJECTED", "ON_HOLD"]).optional(),
});
/** 候補はプロジェクトの配下(ナビの「候補」タブはやめ、「新規プロジェクト ▼」→「候補から作成」から開く。2026-10-15)。 */
export const candidatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projects/candidates",
  validateSearch: candidatesSearch,
  component: CandidatesPage,
});
/** 以前の URL(/candidates)は、候補の画面へ寄せる。 */
const candidatesLegacyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/candidates",
  validateSearch: candidatesSearch,
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/projects/candidates", search });
  },
});
export const customersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/customers",
  component: CustomersPage,
});

// 画面側の出し分け。API 側でも SYSTEM_ADMIN を検証する。
const requireSystemAdmin = async ({ context }: { context: RouterContext }) => {
  const me = await context.queryClient.ensureQueryData({ queryKey: ["me"], queryFn: fetchMe });
  if (!me.user.roles.includes("SYSTEM_ADMIN")) throw redirect({ to: "/" });
};
/** 管理はタブ 1 ページにまとめる(ユーザー要望 2026-09-07)。タブは ?tab= で保持する。 */
export const adminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin",
  validateSearch: z.object({ tab: z.enum(ADMIN_TABS).optional() }),
  beforeLoad: requireSystemAdmin,
  component: AdminPage,
});
/** 旧 URL(/admin/users など)はタブ付きの /admin へ寄せる。 */
const adminLegacyRoute = (path: string, tab: (typeof ADMIN_TABS)[number]) =>
  createRoute({
    getParentRoute: () => appRoute,
    path,
    beforeLoad: () => {
      throw redirect({ to: "/admin", search: { tab } });
    },
  });
export const adminCompanyLinksRoute = adminLegacyRoute("/admin/company-links", "links");
export const adminAnnouncementsRoute = adminLegacyRoute("/admin/announcements", "announcements");

const routeTree = rootRoute.addChildren([
  loginRoute,
  appRoute.addChildren([
    homeRoute,
    projectsRoute,
    projectRoute,
    customersRoute,
    candidatesRoute,
    candidatesLegacyRoute,
    adminRoute,
    adminCompanyLinksRoute,
    adminAnnouncementsRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    // 画面を開く途中のエラーで、真っ白にならないようにする
    defaultErrorComponent: RouteError,
    defaultPendingComponent: RoutePending,
    // 速く開けるときは出さない(ちらつき防止)。0.3 秒を超えたら出す
    defaultPendingMs: 300,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
